import { useEffect, useState, useSyncExternalStore } from 'react';
import { COMBAT, MODE, RIDER_COLORS, RIDE_TUNING } from '@thrasher/shared';
import type { Game } from '../game/Game.js';
import type { HudStore } from '../game/hudStore.js';
import type { Quality } from '../game/Renderer.js';
import { Minimap } from './Minimap.js';

export const CONTROLS: [string, string][] = [
  ['W / ↑', 'Push · hold at a lip to fly out'],
  ['A D / ← →', 'Carve · spin in air · balance on rails'],
  ['S / ↓', 'Brake'],
  ['Space', 'Hold to crouch & pump · release to ollie'],
  ['J', 'Flip trick (+ direction for variations)'],
  ['L / Shift', 'Hold to grab'],
  ['P', 'Punch'],
  ['K', 'Kick'],
  ['R', 'Reset to nearest drop-in'],
  ['Tab', 'Riders'],
  ['M', 'Mute'],
  ['Esc', 'Pause: resume or exit to the game lobby'],
];

function BalanceMeter({ value }: { value: number }): React.JSX.Element {
  const angle = value * 70;
  const danger = Math.abs(value) > 0.7;
  return (
    <div className={`balance ${danger ? 'danger' : ''}`} aria-label="Rail balance">
      <svg viewBox="0 0 200 110" width="200" height="110">
        <defs>
          <linearGradient id="balanceArc" x1="0" x2="1">
            <stop offset="0" stopColor="#ff3b3b" />
            <stop offset="0.3" stopColor="#ffd23f" />
            <stop offset="0.5" stopColor="#7cff4f" />
            <stop offset="0.7" stopColor="#ffd23f" />
            <stop offset="1" stopColor="#ff3b3b" />
          </linearGradient>
        </defs>
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="url(#balanceArc)"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <g transform={`rotate(${angle} 100 100)`}>
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="30"
            stroke="#fff"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <circle cx="100" cy="100" r="8" fill="#fff" />
        </g>
      </svg>
      <span>BALANCE</span>
    </div>
  );
}

function Toast({
  award,
}: {
  award: { id: number; coins: number } | null;
}): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!award) return undefined;
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), 3500);
    return () => clearTimeout(timer);
  }, [award]);
  if (!award || !visible) return null;
  return <div className="award-toast">+{award.coins} coins</div>;
}

function TrickLabel({
  trick,
}: {
  trick: { id: number; text: string } | null;
}): React.JSX.Element | null {
  const [shown, setShown] = useState<{ id: number; text: string } | null>(null);
  useEffect(() => {
    if (!trick) return undefined;
    setShown(trick);
    const timer = setTimeout(() => setShown(null), 1800);
    return () => clearTimeout(timer);
  }, [trick]);
  if (!shown) return null;
  return (
    <div className="trick-label" key={shown.id}>
      {shown.text}
    </div>
  );
}

function HitMarker({ at }: { at: number }): React.JSX.Element | null {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!at) return undefined;
    setShow(true);
    const timer = setTimeout(() => setShow(false), 220);
    return () => clearTimeout(timer);
  }, [at]);
  return show ? <div className="hit-marker" /> : null;
}

export function Hud({ store, game }: { store: HudStore; game: Game }): React.JSX.Element {
  const hud = useSyncExternalStore(store.subscribe, store.get);
  const self = hud.riders.find((rider) => rider.id === hud.selfId);
  const hpFraction = Math.max(0, Math.min(1, hud.hp / COMBAT.maxHp));
  const selfColor = RIDER_COLORS[(self?.color ?? 0) % RIDER_COLORS.length];
  const sorted = [...hud.riders].sort((a, b) => b.kos - a.kos || a.wipeouts - b.wipeouts);
  const setQuality = (quality: Quality) => game.setQuality(quality);

  return (
    <div className="hud">
      <div className="hud-top-left">
        <div className="zone">{hud.zone}</div>
        <div className="online">
          <span className="dot" /> {hud.riders.length} riding
        </div>
      </div>

      <div className="hud-top-right">
        <Minimap game={game} />
      </div>

      <div className="feed" aria-live="polite">
        {hud.feed.map((item) => (
          <div key={item.id} className={`feed-item ${item.tone}`}>
            {item.text}
          </div>
        ))}
      </div>

      {!hud.connected && hud.phase === 'playing' && (
        <div className="banner">Connection lost. Reconnecting…</div>
      )}

      <div className="hud-bottom-left">
        <div className="rider-card">
          <div className="rider-name" style={{ color: selfColor }}>
            {hud.name}
          </div>
          <div className="health" aria-label={`Health ${Math.round(hud.hp)}`}>
            <div className="health-fill" style={{ width: `${hpFraction * 100}%` }} />
            {[0.25, 0.5, 0.75].map((mark) => (
              <div key={mark} className="health-mark" style={{ left: `${mark * 100}%` }} />
            ))}
          </div>
          <div className="rider-meta">
            <span>{RIDE_TUNING[hud.ride].label}</span>
            {hud.coins !== null && <span className="coins">◎ {hud.coins}</span>}
            {hud.protectedSeconds > 0 && <span className="shield">Protected</span>}
          </div>
        </div>
      </div>

      <div className="hud-bottom-center">
        <TrickLabel trick={hud.trick} />
        {hud.balance !== null && <BalanceMeter value={hud.balance} />}
        {hud.charge > 0.05 && hud.mode === MODE.ground && (
          <div className="charge">
            <div className="charge-fill" style={{ width: `${hud.charge * 100}%` }} />
          </div>
        )}
      </div>

      <div className="hud-bottom-right">
        <div className="speed">
          {hud.speed}
          <small>km/h</small>
        </div>
        <div className="attacks">
          <div
            className={`attack ${hud.punchReady >= 1 ? 'ready' : ''}`}
            style={{ '--ready': hud.punchReady } as React.CSSProperties}
          >
            <b>P</b>
            <span>Punch</span>
          </div>
          <div
            className={`attack ${hud.kickReady >= 1 ? 'ready' : ''}`}
            style={{ '--ready': hud.kickReady } as React.CSSProperties}
          >
            <b>K</b>
            <span>Kick</span>
          </div>
        </div>
      </div>

      <HitMarker at={hud.hitMarker} />
      <Toast award={hud.award} />

      {hud.koSeconds !== null && (
        <div className="ko">
          <div className="ko-title">KNOCKED OUT</div>
          <div className="ko-sub">Back on your feet in {hud.koSeconds}</div>
        </div>
      )}

      {hud.scoreboard && (
        <div className="overlay scoreboard">
          <h2>Riders in the park</h2>
          <table>
            <thead>
              <tr>
                <th>Rider</th>
                <th>Ride</th>
                <th>KOs</th>
                <th>Wipeouts</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((rider) => (
                <tr key={rider.id} className={rider.id === hud.selfId ? 'me' : ''}>
                  <td>
                    <span
                      className="swatch"
                      style={{ background: RIDER_COLORS[rider.color % RIDER_COLORS.length] }}
                    />
                    {rider.name}
                    {rider.linked && <span className="linked">hub</span>}
                  </td>
                  <td>{RIDE_TUNING[rider.ride].label}</td>
                  <td>{rider.kos}</td>
                  <td>{rider.wipeouts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hud.help && (
        <div className="overlay help">
          <h2>How to ride</h2>
          <ul className="controls">
            {CONTROLS.map(([key, label]) => (
              <li key={key}>
                <kbd>{key}</kbd>
                <span>{label}</span>
              </li>
            ))}
          </ul>
          <p>
            Airs out of pools and ramps drop you back in. Hold <kbd>W</kbd> as you leave the lip to
            fly out onto the deck. Keep the needle centred with <kbd>A</kbd>/<kbd>D</kbd> while
            grinding.
          </p>
          <button type="button" onClick={() => game.setHelp(false)}>
            Got it
          </button>
        </div>
      )}

      {hud.menu && (
        <div className="overlay menu" role="dialog" aria-label="Paused">
          <h2>Paused</h2>
          <div className="menu-main">
            <button type="button" className="primary" autoFocus onClick={() => game.setMenu(false)}>
              Resume
            </button>
            <button type="button" onClick={() => game.exitToLobby()}>
              Exit to game lobby
            </button>
          </div>
          <div className="menu-settings">
            <div className="ride-row" role="group" aria-label="Graphics quality">
              {(['high', 'medium', 'low'] as const).map((quality) => (
                <button
                  key={quality}
                  type="button"
                  className={`ride-chip ${hud.quality === quality ? 'active' : ''}`}
                  onClick={() => setQuality(quality)}
                >
                  {quality[0]!.toUpperCase() + quality.slice(1)}
                </button>
              ))}
            </div>
            <button type="button" className="ride-chip" onClick={() => game.toggleMute()}>
              {hud.muted ? 'Sound off' : 'Sound on'}
            </button>
          </div>
          <p className="menu-hint">
            <kbd>Esc</kbd> resume · change your ride from the game lobby
          </p>
        </div>
      )}
    </div>
  );
}
